"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useAuth } from "@/hooks/useAuth"
import { listExtraction, getExtractionStatus, listFileInfo, listSurveyInfo, saveFileInfo, type ExtractionVariable, type SurveyInfoRecord } from "@/services/extractionsService"

/**
 * Build the composite identity used by the backend (and by the widget's
 * "is this already added?" check) from a survey id, file name, and variable name.
 *
 * Centralised so callers can't accidentally use a different separator than
 * the DynamoDB sort key format ("var#{surveyId}#{fileName}#{variableName}").
 *
 * @param surveyId - The survey identifier (e.g. "KE2020DHS").
 * @param fileName - The source data file name (e.g. "KEHR7AFL.SAV").
 * @param variableName - The variable name (e.g. "hv105").
 * @returns A stable string key used to dedupe extractions client-side.
 */
export function buildExtractionKey(
  { surveyId, fileName, variableName }: { surveyId: string; fileName: string; variableName: string }
): string {
  return `var#${surveyId}#${fileName}#${variableName}`
}

/** Shape of the context exposed to consumers. */
interface ExtractionsContextValue {
  /** Full list of extracted variables for the current session. */
  variables: ExtractionVariable[]
  /**
   * Set of "{surveyId}#{variableName}" keys for fast O(1) "already added?"
   * lookups from individual widgets.
   */
  addedKeys: Set<string>
  /** True while the initial fetch (or a manual refresh) is in flight. */
  loading: boolean
  /** Force a re-fetch from the backend. Returns a Promise so callers can await it. */
  refresh: () => Promise<void>
  /** Whether an extraction has been previously created for this session. */
  extractionExists: boolean
  /** Mark that an extraction now exists (called after a successful create/update). */
  setExtractionExists: (exists: boolean) => void
  /** S3 prefix where the extraction output lives (e.g. "s3://bucket/sessionId/"). */
  extractionS3Prefix: string
  /** Update the stored S3 prefix (called after a successful create/update). */
  setExtractionS3Prefix: (prefix: string) => void
  /**
   * Map of file keys ("{surveyId}/{fileName}") to user-specified output CSV
   * file names. If a file key is absent, the default output name should be
   * derived from the source file name with a .csv extension.
   */
  outputFileNames: Map<string, string>
  /**
   * Set the output CSV file name for a given file key.
   * Returns false if the name is already in use by another file (duplicate),
   * in which case the update is rejected. Returns true on success.
   *
   * @param fileKey - The composite key "{surveyId}/{fileName}".
   * @param outputName - The desired output file name (must end in .csv).
   * @returns True if the name was set successfully, false if it's a duplicate.
   */
  setOutputFileName: (fileKey: string, outputName: string) => boolean
  /** Map of surveyId → survey metadata (countryName, surveyYear, round, programName). */
  surveyInfo: Map<string, SurveyInfoRecord>
  /** Map of file keys ("{surveyId}/{fileName}") → unit of analysis code. */
  fileUnitOfAnalysis: Map<string, string>
}

// Default value used when a consumer renders outside of a provider. Returning
// an inert "nothing is added, nothing is loading" state keeps the widget
// usable in chats that don't have an extractions panel
const DEFAULT_VALUE: ExtractionsContextValue = {
  variables: [],
  addedKeys: new Set<string>(),
  loading: false,
  refresh: () => Promise.resolve(),
  extractionExists: false,
  setExtractionExists: () => {},
  extractionS3Prefix: "",
  setExtractionS3Prefix: () => {},
  outputFileNames: new Map<string, string>(),
  setOutputFileName: () => false,
  surveyInfo: new Map<string, SurveyInfoRecord>(),
  fileUnitOfAnalysis: new Map<string, string>(),
}

const ExtractionsContext = createContext<ExtractionsContextValue>(DEFAULT_VALUE)

/** Props for the ExtractionsProvider. */
interface ExtractionsProviderProps {
  /** Session whose extraction rows are tracked. */
  sessionId: string
  /**
   * When false the provider does not fetch and exposes an empty list. This
   * lets the consumer keep the provider mounted (avoiding remounts) while
   * the session is in a state where extractions don't apply yet (e.g. the
   * brand-new chat that hasn't been persisted).
   */
  enabled: boolean
  children: ReactNode
}

/**
 * Provider that fetches the session's extraction list once and shares it with
 * every descendant — the right-hand sidebar and every DescribeVariableWidget
 * rendered inline in chat messages. This is the single source of truth
 *
 * @param props - Provider props.
 * @returns React element wrapping children with extraction state.
 */
export function ExtractionsProvider({
  sessionId,
  enabled,
  children,
}: ExtractionsProviderProps): React.JSX.Element {
  const auth = useAuth()
  const [variables, setVariables] = useState<ExtractionVariable[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [extractionExists, setExtractionExists] = useState<boolean>(false)
  const [extractionS3Prefix, setExtractionS3Prefix] = useState<string>("")
  const [outputFileNames, setOutputFileNames] = useState<Map<string, string>>(new Map())
  const [surveyInfo, setSurveyInfo] = useState<Map<string, SurveyInfoRecord>>(new Map())
  const [fileUnitOfAnalysis, setFileUnitOfAnalysis] = useState<Map<string, string>>(new Map())

  /**
   * Derives a default output CSV file name from a source data file name.
   * Used internally to resolve output names for files without an explicit entry.
   *
   * @param sourceFileName - The original data file name (e.g. "KEHR7AFL.SAV").
   * @returns The default output name (e.g. "KEHR7AFL.csv").
   */
  function deriveDefault(sourceFileName: string): string {
    const lastDot = sourceFileName.lastIndexOf(".")
    if (lastDot > 0) {
      return `${sourceFileName.substring(0, lastDot)}.csv`
    }
    return `${sourceFileName}.csv`
  }

  /**
   * Set the output CSV file name for a specific file key.
   * Rejects the update (returns false) if any other file in the session
   * already uses the same output name — either explicitly set or as its
   * derived default. On success, updates local state immediately (optimistic)
   * and persists to the backend via PUT.
   *
   * @param fileKey - The composite key "{surveyId}/{fileName}".
   * @param outputName - The desired output file name (must end in .csv).
   * @returns True if the name was set, false if it conflicts with another file.
   */
  const setOutputFileName = useCallback((fileKey: string, outputName: string): boolean => {
    // Build the set of output names used by OTHER files
    const allFileKeys = new Set<string>()
    for (const v of variables) {
      allFileKeys.add(`${v.surveyId}/${v.fileName}`)
    }

    for (const key of allFileKeys) {
      if (key === fileKey) continue
      const storedName = outputFileNames.get(key)
      const resolvedName = storedName ?? deriveDefault(key.substring(key.indexOf("/") + 1))
      if (resolvedName === outputName) {
        return false
      }
    }

    // Update local state immediately (optimistic)
    setOutputFileNames(prev => {
      const next = new Map(prev)
      next.set(fileKey, outputName)
      return next
    })

    // Persist to backend asynchronously
    const [surveyId, ...fileNameParts] = fileKey.split("/")
    const fileName = fileNameParts.join("/")

    auth.getAccessToken().then((token: string | null) => {
      if (!token) {
        console.error("Cannot persist output file name: no access token")
        return
      }
      return saveFileInfo(
        { sessionId, surveyId, fileName, outputFileName: outputName },
        token
      )
    }).catch((err: unknown) => {
      console.error("Failed to persist output file name, reloading from backend:", err)
      // Re-fetch from backend to revert to the true persisted state
      auth.getAccessToken().then((token: string | null) => {
        if (!token) return
        return listFileInfo(sessionId, token)
      }).then((records) => {
        if (!records) return
        const namesMap = new Map<string, string>()
        for (const record of records) {
          namesMap.set(`${record.surveyId}/${record.fileName}`, record.outputFileName)
        }
        setOutputFileNames(namesMap)
      }).catch((reloadErr: unknown) => {
        console.error("Failed to reload output file names:", reloadErr)
      })
    })

    return true
  }, [sessionId, auth, variables, outputFileNames])

  // Monotonically increasing fetch ID. Each call to fetchExtractions captures
  // the current value; if a newer fetch has started by the time it resolves,
  // the stale result is discarded. This replaces the cancelled-boolean pattern
  // while allowing a single shared fetch function.
  const fetchIdRef = useRef<number>(0)

  /**
   * Fetch all extraction data from the backend. Used by both the initial load
   * effect and the public `refresh` function — single code path, no duplication.
   * Guards against stale responses via the fetchIdRef counter.
   *
   * @returns A Promise that resolves once the fetch completes and state is updated.
   */
  const fetchExtractions = useCallback(async (): Promise<void> => {
    const thisFetchId = ++fetchIdRef.current
    setLoading(true)
    try {
      const token: string | null = await auth.getAccessToken()
      if (!token) {
        if (fetchIdRef.current === thisFetchId) setVariables([])
        return
      }

      const [results, status, fileInfoRecords, surveys] = await Promise.all([
        listExtraction(sessionId, token),
        getExtractionStatus(sessionId, token),
        listFileInfo(sessionId, token).catch((err) => {
          console.warn("Failed to fetch file info:", err)
          return [] as Awaited<ReturnType<typeof listFileInfo>>
        }),
        listSurveyInfo(sessionId, token).catch((err) => {
          console.warn("Failed to fetch survey info:", err)
          return [] as Awaited<ReturnType<typeof listSurveyInfo>>
        }),
      ])

      // Discard results if a newer fetch has been initiated (session switch)
      if (fetchIdRef.current !== thisFetchId) return

      setVariables(results)
      setExtractionExists(status.exists)
      setExtractionS3Prefix(status.s3Prefix || "")

      // Rebuild the file info maps from the backend
      const namesMap = new Map<string, string>()
      const uoaMap = new Map<string, string>()
      for (const record of fileInfoRecords) {
        const fileKey = `${record.surveyId}/${record.fileName}`
        namesMap.set(fileKey, record.outputFileName)
        if (record.unitOfAnalysis) {
          uoaMap.set(fileKey, record.unitOfAnalysis)
        }
      }
      setOutputFileNames(namesMap)
      setFileUnitOfAnalysis(uoaMap)

      // Build the survey info map
      const surveyMap = new Map<string, SurveyInfoRecord>()
      for (const record of surveys) {
        surveyMap.set(record.surveyId, record)
      }
      setSurveyInfo(surveyMap)
    } catch (err) {
      console.error("Failed to fetch extractions:", err)
      if (fetchIdRef.current === thisFetchId) setVariables([])
    } finally {
      if (fetchIdRef.current === thisFetchId) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  /**
   * Initial load: fetch when enabled flips to true or sessionId changes.
   * Resets state immediately then delegates to fetchExtractions which handles
   * stale-response guarding via the fetchIdRef counter.
   */
  useEffect(() => {
    if (!enabled) {
      setVariables([])
      setExtractionExists(false)
      setExtractionS3Prefix("")
      setOutputFileNames(new Map())
      setSurveyInfo(new Map())
      setFileUnitOfAnalysis(new Map())
      return
    }

    // Reset immediately on session change to avoid stale UI
    setExtractionExists(false)
    setExtractionS3Prefix("")
    setOutputFileNames(new Map())
    setSurveyInfo(new Map())
    setFileUnitOfAnalysis(new Map())

    fetchExtractions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, enabled])

  /**
   * Public refresh — awaitable so callers (e.g. the widget after a PUT) can
   * keep their spinner up until the list is reconciled with the backend.
   */
  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return
    await fetchExtractions()
  }, [enabled, fetchExtractions])

  // Derive the fast-lookup set from the variable list whenever it changes.
  const addedKeys: Set<string> = useMemo(() => {
    return new Set(
      variables.map(v =>
        buildExtractionKey({ surveyId: v.surveyId, fileName: v.fileName, variableName: v.variableName })
      )
    )
  }, [variables])

  const value: ExtractionsContextValue = useMemo(
    () => ({ variables, addedKeys, loading, refresh, extractionExists, setExtractionExists, extractionS3Prefix, setExtractionS3Prefix, outputFileNames, setOutputFileName, surveyInfo, fileUnitOfAnalysis }),
    [variables, addedKeys, loading, refresh, extractionExists, extractionS3Prefix, outputFileNames, setOutputFileName, surveyInfo, fileUnitOfAnalysis]
  )

  return (
    <ExtractionsContext.Provider value={value}>{children}</ExtractionsContext.Provider>
  )
}

/**
 * Hook for consumers (sidebar, DescribeVariableWidget) to read the shared
 * extraction state.
 *
 * @returns The current ExtractionsContext value.
 */
export function useExtractions(): ExtractionsContextValue {
  return useContext(ExtractionsContext)
}
